// stores/authStore.js
// Auth Store - Synchronized with Mobile App (adapted for web with localStorage)
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      lastLoginTime: null,
      lastActivityTime: null,
      sessionExpired: false,

      login: (user, token, refreshToken = null) => {
        const now = new Date().toISOString();
        set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          lastLoginTime: now,
          lastActivityTime: now,
          sessionExpired: false,
        });
      },

      touchActivity: () => {
        const { isAuthenticated } = get();
        if (!isAuthenticated) return;
        set({ lastActivityTime: new Date().toISOString() });
      },

      logout: (sessionExpired = false) => {
        // Clear localStorage
        localStorage.removeItem("auth-storage");
        
        // Clear React Query cache if available
        if (window.queryClient) {
          window.queryClient.clear();
        }

        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          lastLoginTime: null,
          lastActivityTime: null,
          sessionExpired,
        });
      },

      updateUser: (userData) =>
        set((state) => ({
          user: { ...state.user, ...userData },
        })),

      updateToken: (token, refreshToken = null) =>
        set({
          token,
          ...(refreshToken && { refreshToken }),
        }),

      setLoading: (isLoading) => set({ isLoading }),

      clearSessionExpired: () => set({ sessionExpired: false }),

      // Helper methods
      getAuthHeader: () => {
        const { token } = get();
        return token ? `Bearer ${token}` : null;
      },

      isTokenExpired: () => {
        const { token, lastLoginTime } = get();
        if (!token || !lastLoginTime) return true;

        // Check if token is older than 24 hours
        const loginTime = new Date(lastLoginTime);
        const now = new Date();
        const hoursDiff = (now - loginTime) / (1000 * 60 * 60);

        return hoursDiff > 24;
      },

      isSessionInactive: () => {
        const { isAuthenticated, lastActivityTime, lastLoginTime } = get();
        if (!isAuthenticated) return false;

        const reference = lastActivityTime || lastLoginTime;
        if (!reference) return true;

        const inactiveMs = Date.now() - new Date(reference).getTime();
        return inactiveMs >= INACTIVITY_TIMEOUT_MS;
      },

      clearAuthData: () =>
        set({
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          sessionExpired: false,
        }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      // Only persist essential data
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        lastLoginTime: state.lastLoginTime,
        lastActivityTime: state.lastActivityTime,
      }),
    }
  )
);

